// Cliente local — substitui o @supabase/supabase-js por SQLite.
//
// O App.js falava direto com o Supabase em 14 pontos. Em vez de reescrever
// todos, este modulo expoe a MESMA superficie de cadeia e traduz para as rotas
// /api/db do proprio servidor (server/server.js), que le o SQLite.
//
// Suporta o que o App.js realmente usa:
//   .from(t).select('*').eq(col, v).order(col)
//   .from(t).select('*, em_andamento!left(observacoes)').eq(...).order(...)
//   .from(t).select('*').eq(col, v).single()
//   .from(t).insert(obj)
//   .from(t).upsert(obj, { onConflict: 'id_tarefa' })
//   .from(t).update(obj).eq(col, v)
//   .from(t).delete().eq(col, v)

const API = process.env.REACT_APP_API_URL || '/api';

async function req(caminho, opts = {}) {
  try {
    const r = await fetch(`${API}${caminho}`, {
      headers: { 'Content-Type': 'application/json' },
      ...opts,
    });
    const corpo = await r.json().catch(() => ({}));
    if (!r.ok) return { data: null, error: { message: corpo.error || `HTTP ${r.status}` } };
    return { data: corpo.data ?? null, error: null };
  } catch (e) {
    return { data: null, error: { message: e.message } };
  }
}

// Converte o select do PostgREST em (colunas, embed).
// "*, em_andamento!left(observacoes)" -> embed "em_andamento:observacoes"
function parseSelect(texto) {
  if (!texto) return { embed: null };
  const m = String(texto).match(/([a-z_]+)!(?:left|inner)\(([^)]*)\)/i);
  if (!m) return { embed: null };
  return { embed: `${m[1]}:${m[2].split(',').map(c => c.trim()).join(',')}` };
}

class Query {
  constructor(tabela) {
    this.tabela = tabela;
    this.op = 'select';
    this.embed = null;
    this.eqCol = null;
    this.eqVal = null;
    this.ordem = null;
    this.unico = false;
    this.payload = null;
    this.onConflict = null;
  }

  select(texto = '*') {
    if (this.op === 'select') this.embed = parseSelect(texto).embed;
    return this;
  }

  insert(obj) { this.op = 'insert'; this.payload = obj; return this; }

  upsert(obj, { onConflict } = {}) {
    this.op = 'upsert'; this.payload = obj; this.onConflict = onConflict || null; return this;
  }

  update(obj) { this.op = 'update'; this.payload = obj; return this; }

  delete() { this.op = 'delete'; return this; }

  eq(coluna, valor) { this.eqCol = coluna; this.eqVal = valor; return this; }

  order(coluna) { this.ordem = coluna; return this; }

  single() { this.unico = true; return this; }

  async run() {
    const { tabela, eqCol, eqVal } = this;

    if (this.op === 'insert' || this.op === 'upsert') {
      return req(`/db/${tabela}`, {
        method: 'POST',
        body: JSON.stringify({
          modo: this.op,
          linhas: this.payload,
          onConflict: this.onConflict,
        }),
      });
    }

    if (this.op === 'update') {
      return req(`/db/${tabela}`, {
        method: 'PATCH',
        body: JSON.stringify({ valores: this.payload, eqCol, eqVal }),
      });
    }

    if (this.op === 'delete') {
      const qs = new URLSearchParams({ eqCol: eqCol ?? '', eqVal: String(eqVal ?? '') });
      return req(`/db/${tabela}?${qs}`, { method: 'DELETE' });
    }

    const qs = new URLSearchParams();
    if (eqCol) { qs.set('eqCol', eqCol); qs.set('eqVal', String(eqVal ?? '')); }
    if (this.ordem) qs.set('order', this.ordem);
    if (this.embed) qs.set('embed', this.embed);
    if (this.unico) qs.set('single', 'true');
    const q = qs.toString();
    return req(`/db/${tabela}${q ? `?${q}` : ''}`);
  }

  then(resolve, reject) { return this.run().then(resolve, reject); }
  catch(cb) { return this.run().catch(cb); }
}

// Mesma forma do createClient — o App.js nao precisa saber da troca.
export function createClient() {
  return { from: (tabela) => new Query(tabela) };
}

export default createClient;
